import React from "react";

/**
 * Six brand-styled operator avatars for the AI assistant.
 * All inline SVG, no external assets — fuchsia/cyan neon linework on transparent bg.
 * 2 masculine · 2 feminine · 2 androgynous  (deliberately no built-in names — the user picks)
 */

const F = "#e879f9"; // meye-fuchsia
const C = "#22d3ee"; // meye-cyan
const N = "#f9a8d4"; // soft pink accent

// Shared halo behind every avatar
const Halo = ({ tint = F }) => (
    <>
        <defs>
            <radialGradient id={`halo-${tint.slice(1)}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={tint} stopOpacity="0.4" />
                <stop offset="70%" stopColor={tint} stopOpacity="0.05" />
                <stop offset="100%" stopColor={tint} stopOpacity="0" />
            </radialGradient>
        </defs>
        <circle cx="60" cy="60" r="58" fill={`url(#halo-${tint.slice(1)})`} />
    </>
);

// 1. Masculine — angular jaw, undercut hair, cyan visor
const AvatarMasculine1 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={C} />
        {/* head */}
        <path d="M40 52 Q40 30 60 30 Q80 30 80 52 L80 75 Q80 92 60 92 Q40 92 40 75 Z" fill="#111" stroke={C} strokeWidth="2" />
        {/* hair (undercut) */}
        <path d="M38 50 Q40 22 60 22 Q80 22 82 50 L82 40 Q75 32 60 32 Q45 32 38 40 Z" fill={C} opacity="0.85" />
        {/* visor across eyes */}
        <rect x="42" y="55" width="36" height="7" rx="2" fill={F} opacity="0.95" />
        <line x1="42" y1="58.5" x2="78" y2="58.5" stroke="#000" strokeWidth="1.2" opacity="0.4" />
        {/* jaw */}
        <line x1="46" y1="80" x2="54" y2="88" stroke={C} strokeWidth="1.5" opacity="0.6" />
        <line x1="74" y1="80" x2="66" y2="88" stroke={C} strokeWidth="1.5" opacity="0.6" />
        {/* mouth */}
        <line x1="54" y1="76" x2="66" y2="76" stroke={F} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// 2. Feminine — flowing long hair, softer face, fuchsia lip
const AvatarFeminine1 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={F} />
        {/* hair back */}
        <path d="M28 60 Q26 32 60 26 Q94 32 92 60 L94 100 Q80 96 74 100 L46 100 Q40 96 26 100 Z" fill={F} opacity="0.75" />
        {/* face */}
        <path d="M42 55 Q42 34 60 34 Q78 34 78 55 L78 78 Q78 92 60 92 Q42 92 42 78 Z" fill="#111" stroke={F} strokeWidth="2" />
        {/* hair fringe */}
        <path d="M40 46 Q45 32 60 32 Q78 32 80 48 Q75 42 60 42 Q50 42 40 46 Z" fill={F} />
        {/* eyes */}
        <circle cx="51" cy="60" r="3" fill={C} />
        <circle cx="69" cy="60" r="3" fill={C} />
        {/* eyebrows */}
        <line x1="46" y1="53" x2="56" y2="52" stroke={F} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="64" y1="52" x2="74" y2="53" stroke={F} strokeWidth="1.5" strokeLinecap="round" />
        {/* lips */}
        <path d="M52 78 Q60 82 68 78" stroke={F} strokeWidth="2.2" fill="none" strokeLinecap="round" />
    </svg>
);

// 3. Masculine — buzz cut, beard stubble, warm accent
const AvatarMasculine2 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={F} />
        {/* head */}
        <path d="M40 55 Q40 32 60 32 Q80 32 80 55 L80 76 Q80 90 60 90 Q40 90 40 76 Z" fill="#111" stroke={F} strokeWidth="2" />
        {/* buzz cut top */}
        <path d="M40 45 Q42 30 60 30 Q78 30 80 45 L80 42 Q65 34 40 42 Z" fill={F} opacity="0.35" />
        <line x1="43" y1="38" x2="50" y2="35" stroke={F} strokeWidth="0.9" opacity="0.7" />
        <line x1="55" y1="34" x2="60" y2="32" stroke={F} strokeWidth="0.9" opacity="0.7" />
        <line x1="65" y1="34" x2="70" y2="35" stroke={F} strokeWidth="0.9" opacity="0.7" />
        <line x1="72" y1="36" x2="77" y2="38" stroke={F} strokeWidth="0.9" opacity="0.7" />
        {/* eyes */}
        <circle cx="52" cy="60" r="2.5" fill={C} />
        <circle cx="68" cy="60" r="2.5" fill={C} />
        {/* stubble */}
        <path d="M46 76 Q60 82 74 76 Q70 86 60 87 Q50 86 46 76 Z" fill={F} opacity="0.18" />
        {/* mouth */}
        <line x1="54" y1="78" x2="66" y2="78" stroke={C} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// 4. Feminine — sleek bob, sharp eyeliner
const AvatarFeminine2 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={C} />
        {/* hair back */}
        <path d="M32 40 Q30 26 60 24 Q90 26 88 40 L92 82 L74 88 L74 55 L46 55 L46 88 L28 82 Z" fill={C} opacity="0.8" />
        {/* face */}
        <path d="M44 52 Q44 34 60 34 Q76 34 76 52 L76 78 Q76 90 60 90 Q44 90 44 78 Z" fill="#111" stroke={C} strokeWidth="2" />
        {/* eyeliner sweep */}
        <path d="M46 58 Q52 55 58 58 L58 62 Q52 60 46 62 Z" fill={F} />
        <path d="M62 58 Q68 55 74 58 L74 62 Q68 60 62 62 Z" fill={F} />
        {/* pupils */}
        <circle cx="52" cy="61" r="1.6" fill={C} />
        <circle cx="68" cy="61" r="1.6" fill={C} />
        {/* lips */}
        <path d="M50 78 L60 82 L70 78 L66 74 L60 76 L54 74 Z" fill={F} opacity="0.9" />
    </svg>
);

// 5. Androgynous — medium wavy hair, neutral features, dual-tone
const AvatarAndrogynous1 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={F} />
        {/* wavy hair back */}
        <path d="M32 55 Q30 30 60 26 Q90 30 88 55 L88 78 Q84 72 80 78 L80 60 Q60 55 40 60 L40 78 Q36 72 32 78 Z" fill={C} opacity="0.65" />
        {/* face */}
        <path d="M42 54 Q42 34 60 34 Q78 34 78 54 L78 78 Q78 91 60 91 Q42 91 42 78 Z" fill="#111" stroke={F} strokeWidth="2" />
        {/* eyes — heterochromia (one cyan, one fuchsia) */}
        <circle cx="52" cy="60" r="3" fill={C} />
        <circle cx="68" cy="60" r="3" fill={F} />
        {/* small nose bridge */}
        <line x1="60" y1="66" x2="60" y2="72" stroke={F} strokeWidth="1" opacity="0.5" />
        {/* neutral mouth */}
        <line x1="52" y1="79" x2="68" y2="79" stroke={C} strokeWidth="2" strokeLinecap="round" />
        {/* single ear cuff */}
        <circle cx="78" cy="70" r="1.6" fill={F} />
    </svg>
);

// 6. Androgynous — hooded/cloaked figure with glowing eye (mysterious)
const AvatarAndrogynous2 = () => (
    <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <Halo tint={C} />
        {/* hood outline */}
        <path d="M30 88 Q26 40 60 32 Q94 40 90 88 L82 92 Q80 78 76 74 L44 74 Q40 78 38 92 Z" fill={C} opacity="0.55" />
        {/* face in shadow */}
        <path d="M44 54 Q44 38 60 38 Q76 38 76 54 L76 74 L44 74 Z" fill="#050505" stroke={C} strokeWidth="1.5" />
        {/* single glowing horizontal aperture eye */}
        <rect x="47" y="58" width="26" height="4" rx="1.5" fill={F} />
        <rect x="47" y="58" width="26" height="4" rx="1.5" fill={F} opacity="0.5" filter="url(#blur1)" />
        <defs>
            <filter id="blur1"><feGaussianBlur stdDeviation="2" /></filter>
        </defs>
        {/* subtle chin outline */}
        <path d="M50 70 Q60 74 70 70" stroke={C} strokeWidth="1" fill="none" opacity="0.5" />
        {/* rune on forehead */}
        <path d="M56 46 L60 50 L64 46 L60 42 Z" fill="none" stroke={F} strokeWidth="1.2" />
    </svg>
);

// Registry — key is stored in DB, value is the React component
export const AVATARS = [
    { id: "m1", label: "operator α", Comp: AvatarMasculine1 },
    { id: "f1", label: "operator β", Comp: AvatarFeminine1 },
    { id: "m2", label: "operator γ", Comp: AvatarMasculine2 },
    { id: "f2", label: "operator δ", Comp: AvatarFeminine2 },
    { id: "a1", label: "operator ε", Comp: AvatarAndrogynous1 },
    { id: "a2", label: "operator ζ", Comp: AvatarAndrogynous2 },
];

export const DEFAULT_AVATAR = "a1";

/** Render a single avatar by its stored id. Falls back to DEFAULT_AVATAR. */
export const AssistantAvatar = ({ id, size = 40, className = "" }) => {
    const entry = AVATARS.find((a) => a.id === id) || AVATARS.find((a) => a.id === DEFAULT_AVATAR);
    const Comp = entry.Comp;
    return (
        <span
            className={`inline-block ${className}`}
            style={{ width: size, height: size }}
            data-testid={`assistant-avatar-${entry.id}`}
            aria-label={entry.label}
        >
            <Comp />
        </span>
    );
};

/** Grid of 6 avatars for the picker. Renders the selected one with a fuchsia ring. */
export const AvatarPicker = ({ value, onChange, size = 76 }) => {
    return (
        <div className="grid grid-cols-3 gap-3" data-testid="avatar-picker">
            {AVATARS.map((a) => {
                const active = value === a.id;
                const Comp = a.Comp;
                return (
                    <button
                        key={a.id}
                        type="button"
                        onClick={() => onChange(a.id)}
                        className={`relative rounded-2xl p-2 flex flex-col items-center gap-1 transition-all ${
                            active
                                ? "bg-meye-fuchsia/15 border-2 border-meye-fuchsia shadow-[0_0_20px_rgba(217,70,239,0.4)]"
                                : "bg-black/40 border border-meye-border hover:border-meye-cyan/60"
                        }`}
                        data-testid={`avatar-choice-${a.id}`}
                        aria-label={`Choose ${a.label}`}
                        aria-pressed={active}
                    >
                        <span
                            className="inline-block"
                            style={{ width: size, height: size }}
                        >
                            <Comp />
                        </span>
                        <span
                            className={`text-[9px] uppercase tracking-widest font-mono ${
                                active ? "text-meye-fuchsia" : "text-zinc-500"
                            }`}
                        >
                            {a.label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default AssistantAvatar;
